import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Folder, RefreshCw, Trash2, BarChart3 } from "lucide-react";
import { SavedScenario } from "@/hooks/useSavedScenarios";
import { cn } from "@/lib/utils";

interface SavedScenariosPanelProps {
  scenarios: SavedScenario[];
  selectedForCompare: string[];
  onToggleCompare: (id: string) => void;
  onLoad: (scenario: SavedScenario) => void;
  onDelete: (id: string) => void;
  onStartCompare: () => void;
  language: string;
  formatCurrency: (value: number) => string;
}

const SavedScenariosPanel = ({
  scenarios,
  selectedForCompare,
  onToggleCompare,
  onLoad,
  onDelete,
  onStartCompare,
  language,
  formatCurrency
}: SavedScenariosPanelProps) => {
  if (scenarios.length === 0) {
    return (
      <Card className="p-4 bg-muted/30 border-dashed">
        <div className="text-center text-muted-foreground">
          <Folder className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            {language === 'sv'
              ? 'Inga sparade scenarier ännu'
              : 'No saved scenarios yet'}
          </p>
          <p className="text-xs mt-1">
            {language === 'sv'
              ? 'Spara din nuvarande kalkyl för att jämföra senare'
              : 'Save your current calculation to compare later'}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-xs sm:text-sm flex items-center gap-2">
          <Folder className="h-3.5 w-3.5" />
          {language === 'sv' ? 'Sparade scenarier' : 'Saved Scenarios'}
        </h4>
        <Badge variant="outline" className="text-xs">{scenarios.length}/5</Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {scenarios.map((scenario) => (
          <motion.div
            key={scenario.id}
            className={cn(
              "flex items-center gap-2 p-2 rounded-lg border transition-colors",
              selectedForCompare.includes(scenario.id)
                ? "border-brand-500 bg-brand-50/50 dark:bg-brand-900/20"
                : "border-border hover:border-brand-300"
            )}
            whileHover={{ scale: 1.01 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Checkbox
              checked={selectedForCompare.includes(scenario.id)}
              onCheckedChange={() => onToggleCompare(scenario.id)}
              className="shrink-0"
            />

            <div
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: scenario.color }}
            />

            <div className="flex-1 min-w-0">
              <p className="font-medium text-xs sm:text-sm truncate">{scenario.name}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {scenario.inputs.numberOfProperties} {language === 'sv' ? 'fast.' : 'prop.'} · {formatCurrency(scenario.results.totalYearly)}/yr
              </p>
            </div>

            <div className="flex gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onLoad(scenario)}
                title={language === 'sv' ? 'Ladda detta scenario' : 'Load this scenario'}
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => onDelete(scenario.id)}
                title={language === 'sv' ? 'Ta bort' : 'Delete'}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {selectedForCompare.length >= 2 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button className="w-full mt-3" size="sm" onClick={onStartCompare}>
            <BarChart3 className="h-4 w-4 mr-2" />
            {language === 'sv'
              ? `Jämför ${selectedForCompare.length} scenarier`
              : `Compare ${selectedForCompare.length} Scenarios`}
          </Button>
        </motion.div>
      )}
    </Card>
  );
};

export default SavedScenariosPanel;
